/**
 * CampaignPanel — the Adaptive Sampling tool as a Thread Explore-panel tab.
 *
 * Within any Thread project, the user can open this panel to configure and
 * run an adaptive-sampling campaign that feeds the solver (or its mock in
 * test mode). One campaign per project — the association is stored in a
 * small zustand store with a localStorage bridge so reloads keep the link.
 *
 * When no campaign exists yet: shows the configurator inline.
 * After creation: shows the campaign view (header + tab switcher).
 */

import { useMemo } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useQueryClient } from "@tanstack/react-query";
import { Box, Button, Flex, Text } from "@radix-ui/themes";

import CampaignConfigurator from "../../features/adaptive-sampling/configurator/CampaignConfigurator";
import { CampaignView } from "../../features/adaptive-sampling/views";
import {
  adaptiveSamplingKeys,
  useCampaignStateQuery,
} from "../../features/adaptive-sampling/api";

// ---------------------------------------------------------------------------
// Project ↔ campaign association
// ---------------------------------------------------------------------------

type LinkStore = {
  byProject: Record<string, string>;
  link: (projectId: string, campaignId: string) => void;
  unlink: (projectId: string) => void;
};

const useProjectCampaignLink = create<LinkStore>()(
  persist(
    (set) => ({
      byProject: {},
      link: (projectId, campaignId) =>
        set((s) => ({
          byProject: { ...s.byProject, [projectId]: campaignId },
        })),
      unlink: (projectId) =>
        set((s) => {
          const next = { ...s.byProject };
          delete next[projectId];
          return { byProject: next };
        }),
    }),
    { name: "thread-adaptive-sampling-project-links" },
  ),
);

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export interface CampaignPanelProps {
  projectId: string;
}

export default function CampaignPanel({ projectId }: CampaignPanelProps) {
  const link = useProjectCampaignLink((s) => s.byProject[projectId]);
  const linkFn = useProjectCampaignLink((s) => s.link);
  const unlinkFn = useProjectCampaignLink((s) => s.unlink);
  const queryClient = useQueryClient();

  const stateQuery = useCampaignStateQuery(link);
  // If the link points at a campaign the mock lost (e.g. after a hard
  // refresh wiping the in-memory store), treat it as "no campaign" so the
  // configurator shows up instead of an infinite spinner.
  const lostCampaign = useMemo(() => {
    return Boolean(
      link &&
        stateQuery.isError &&
        /not.?found/i.test((stateQuery.error as Error | null)?.message ?? ""),
    );
  }, [link, stateQuery.isError, stateQuery.error]);

  if (!link || lostCampaign) {
    return (
      <Box style={{ height: "100%", overflow: "auto" }}>
        <CampaignConfigurator
          mode="panel"
          onCreated={(campaignId) => {
            linkFn(projectId, campaignId);
            queryClient.invalidateQueries({
              queryKey: adaptiveSamplingKeys.campaignList(),
            });
          }}
        />
      </Box>
    );
  }

  return (
    <Flex direction="column" style={{ height: "100%", minHeight: 0 }}>
      <Flex
        align="center"
        justify="between"
        px="3"
        py="1"
        style={{
          borderBottom: "1px solid var(--gray-a4)",
          background: "var(--color-panel-solid)",
        }}
      >
        <Text size="1" color="gray">
          Adaptive sampling campaign · bound to this project
        </Text>
        <Button
          size="1"
          variant="ghost"
          color="gray"
          onClick={() => unlinkFn(projectId)}
        >
          Unlink
        </Button>
      </Flex>
      <Box style={{ flex: 1, minHeight: 0 }}>
        <CampaignView mode="panel" campaignIdOverride={link} />
      </Box>
    </Flex>
  );
}
